import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import { Box, Button, MenuItem, Select, Typography } from '@mui/material'
import { useState } from 'react'

import { useAppContext } from '@/hooks'
import { ReleaseStatusEnum } from '@/rtkApi/mddApiSliceGeneratedV2'
import { CATALOG_UI } from '@/styles/catalogUi'

import { PackageIcon } from '../PackageIcon'
import { StatusPill } from '../StatusPill'
import { DeploymentPackageRecord, STATUS_PILL } from '../types'
import { ConfirmDialog } from './ConfirmDialog'

const STATUSES: ReleaseStatusEnum[] = ['OPEN', 'COMPLETED', 'CANCEL']

type PackageHeaderProps = {
	canEdit: boolean
	onChangeStatus: (status: ReleaseStatusEnum) => void
	onSync: () => void
	pkg: DeploymentPackageRecord
	syncing: boolean
	systemName: string
}

export const PackageHeader = ({
	canEdit,
	onChangeStatus,
	onSync,
	pkg,
	syncing,
	systemName,
}: PackageHeaderProps) => {
	const { t } = useAppContext()
	const [pendingStatus, setPendingStatus] = useState<ReleaseStatusEnum | null>(null)
	const status = STATUS_PILL[pkg.status]

	return (
		<>
			<Typography sx={{ color: '#9A9A9A', fontSize: 12, mb: 1.25 }}>
				{`${systemName ? `${systemName} > ` : ''}${t('ACTIVITY_BAR_DEPLOYMENT_PACKAGES')} > `}
				<Box component="span" sx={{ color: '#1F4954', fontWeight: 700 }}>
					{pkg.name}
				</Box>
			</Typography>

			<Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1.5, mb: 1 }}>
				<Box
					sx={{
						alignItems: 'center',
						bgcolor: status.background,
						borderRadius: '9px',
						color: status.color,
						display: 'flex',
						height: 38,
						justifyContent: 'center',
						width: 38,
					}}
				>
					<PackageIcon sx={{ fontSize: 20 }} />
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						component="h1"
						sx={{ color: CATALOG_UI.colors.title, fontSize: 22, fontWeight: 700 }}
					>
						{pkg.name}
					</Typography>
					<Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
						<StatusPill status={pkg.status} />
						{pkg.owner ? (
							<Typography sx={{ color: '#9A9A9A', fontSize: 12 }}>{pkg.owner}</Typography>
						) : null}
					</Box>
				</Box>
				<Button
					variant="outlined"
					startIcon={<InventoryOutlinedIcon sx={{ fontSize: 14 }} />}
					disabled={!canEdit || syncing || !pkg.branchId}
					onClick={onSync}
					data-testid="deployment-package-sync-branch"
					sx={{ borderRadius: '8px', height: 34, textTransform: 'none', whiteSpace: 'nowrap' }}
				>
					{t('BRANCHES_UPDATE_DEPLOYMENT_PACKAGE')}
				</Button>
				<Select
					size="small"
					value={pkg.status}
					disabled={!canEdit}
					onChange={(event) => {
						const next = event.target.value as ReleaseStatusEnum
						if (next !== pkg.status) setPendingStatus(next)
					}}
					data-testid="deployment-package-status"
					sx={{ borderRadius: '8px', fontSize: 13, height: 34, minWidth: 150 }}
				>
					{STATUSES.map((value) => (
						<MenuItem key={value} value={value} sx={{ fontSize: 13 }}>
							{t(STATUS_PILL[value].labelKey)}
						</MenuItem>
					))}
				</Select>
			</Box>

			<ConfirmDialog
				open={pendingStatus != null}
				title={t('STATUS_MODAL_HEADER', [pkg.name])}
				message={`${t('STATUS_MODAL_MESSAGE_1')} ${
					pendingStatus ? t(STATUS_PILL[pendingStatus].labelKey) : ''
				}. ${t('STATUS_MODAL_MESSAGE_2')}`}
				confirmLabel={t('CONFIRM')}
				onClose={() => setPendingStatus(null)}
				onConfirm={() => {
					if (pendingStatus) onChangeStatus(pendingStatus)
					setPendingStatus(null)
				}}
			/>
		</>
	)
}
